import type { OralExamBookingRunSummary } from '@/common/api/models/oralExamBookingRunSummary';
import { OralExamReadiness } from '@/common/api/models/oralExamReadiness';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import { isCohortExamSurface } from '@/common/utils/oralExam/runConduct';
import { unwrapOralExamCohortState } from './unwrapOralExamCohortState';
import { unwrapOralExamRunState } from './useOralExamRunStatePoll';

type IdReviewLagEvidence = {
  idPhotoBlobPath: string | null;
  idSelfiePhotoBlobPath: string | null;
};

/** Pre-decision blob paths for stale GET detection (cleared when evidence changes). */
const idReviewLagEvidenceByBooking = new Map<string, IdReviewLagEvidence>();

/** Test-only reset for lag-evidence map. */
export function clearIdReviewLagEvidenceForTests(): void {
  idReviewLagEvidenceByBooking.clear();
}

function recordIdReviewLagEvidence(
  bookingId: string,
  summary: Pick<OralExamBookingRunSummary, 'idPhotoBlobPath' | 'idSelfiePhotoBlobPath'>,
): void {
  idReviewLagEvidenceByBooking.set(bookingId, {
    idPhotoBlobPath: summary.idPhotoBlobPath ?? null,
    idSelfiePhotoBlobPath: summary.idSelfiePhotoBlobPath ?? null,
  });
}

function incomingMatchesLagEvidence(
  incomingRun: OralExamBookingRunSummary,
  evidence: IdReviewLagEvidence,
): boolean {
  return (incomingRun.idPhotoBlobPath ?? null) === evidence.idPhotoBlobPath
    && (incomingRun.idSelfiePhotoBlobPath ?? null) === evidence.idSelfiePhotoBlobPath;
}

/**
 * Derive pre-flight readiness after an ID approve/reject, matching
 * `resolveEffectiveReadiness` in StudentLiveTile for the ID branch.
 */
export function derivePreflightReadinessAfterIdReview(
  summary: Pick<OralExamBookingRunSummary, 'idVerification' | 'runState' | 'present'>,
): typeof OralExamReadiness[keyof typeof OralExamReadiness] {
  if (summary.present !== true) return OralExamReadiness.not_joined;

  const idStatus = summary.idVerification?.status;
  if (idStatus === 'rejected') return OralExamReadiness.id_rejected;
  if (idStatus === 'submitted') return OralExamReadiness.awaiting_id_review;
  if (idStatus === 'approved') {
    const { runState } = summary;
    if (!runState.preflightLobbyReadyAt) return OralExamReadiness.in_lobby;
    if (isCohortExamSurface(runState) && !runState.orientationCompletedAt) {
      return OralExamReadiness.watching_orientation;
    }
    return OralExamReadiness.ready_to_start;
  }
  return OralExamReadiness.in_lobby;
}

function patchBookingRun(
  run: OralExamBookingRunSummary,
  runState: OralExamRunStateResponse,
): OralExamBookingRunSummary {
  const idVerification = runState.idVerification ?? run.idVerification;
  const patched: OralExamBookingRunSummary = {
    ...run,
    idVerification,
    idPhotoBlobPath: runState.idPhotoBlobPath ?? null,
    idSelfiePhotoBlobPath: runState.idSelfiePhotoBlobPath ?? null,
    runState: { ...run.runState, ...runState },
    readiness: undefined,
  };
  patched.readiness = derivePreflightReadinessAfterIdReview(patched);
  return patched;
}

function isStaleIdReviewPoll(
  previousStatus: string | undefined,
  incomingStatus: string | undefined,
): boolean {
  const snapped = previousStatus === 'approved' || previousStatus === 'rejected';
  const regressed = incomingStatus === 'submitted'
    || incomingStatus === 'not_submitted'
    || incomingStatus === undefined;
  return snapped && regressed;
}

/**
 * When a cohort poll/refetch still returns `submitted` but the cache already
 * holds an approve/reject snap from the POST body, keep the snapped ID row.
 */
export function mergeCohortCachePreservingIdReview(
  previous: unknown,
  incoming: unknown,
): unknown {
  const previousCohort = unwrapOralExamCohortState(previous);
  const incomingCohort = unwrapOralExamCohortState(incoming);
  if (!previousCohort?.bookingRuns?.length || !incomingCohort?.bookingRuns) {
    return incoming;
  }

  let changed = false;
  const bookingRuns = incomingCohort.bookingRuns.map((incomingRun) => {
    const previousRun = previousCohort.bookingRuns?.find(
      (run) => run.bookingId === incomingRun.bookingId,
    );
    if (!previousRun) return incomingRun;
    if (!isStaleIdReviewPoll(
      previousRun.idVerification?.status,
      incomingRun.idVerification?.status,
    )) {
      return incomingRun;
    }

    const evidence = idReviewLagEvidenceByBooking.get(incomingRun.bookingId);
    if (!evidence || !incomingMatchesLagEvidence(incomingRun, evidence)) {
      idReviewLagEvidenceByBooking.delete(incomingRun.bookingId);
      return incomingRun;
    }

    changed = true;
    const snappedRunState: OralExamRunStateResponse = {
      ...incomingRun.runState,
      ...previousRun.runState,
      idVerification: previousRun.idVerification ?? previousRun.runState.idVerification,
      idPhotoBlobPath: previousRun.idPhotoBlobPath ?? null,
      idSelfiePhotoBlobPath: previousRun.idSelfiePhotoBlobPath ?? null,
    };
    return patchBookingRun(incomingRun, snappedRunState);
  });

  if (!changed) return incoming;

  const envelope = incoming as { data?: { data?: unknown } };
  return {
    ...envelope,
    data: {
      ...envelope.data,
      data: {
        ...incomingCohort,
        bookingRuns,
      },
    },
  };
}

/**
 * Merge an approve/reject POST run-state payload into the cached cohort aggregate
 * for one booking. Returns the original cache when nothing can be patched.
 */
export function applyIdReviewToCohortCache(
  cached: unknown,
  bookingId: string,
  mutationPayload: unknown,
): unknown {
  const runState = unwrapOralExamRunState(mutationPayload);
  if (!runState) return cached;

  const cohort = unwrapOralExamCohortState(cached);
  if (!cohort?.bookingRuns) return cached;

  const existingRun = cohort.bookingRuns.find((run) => run.bookingId === bookingId);
  if (
    existingRun
    && (runState.idVerification?.status === 'approved' || runState.idVerification?.status === 'rejected')
  ) {
    recordIdReviewLagEvidence(bookingId, existingRun);
  }

  const bookingRuns = cohort.bookingRuns.map((run) => (
    run.bookingId === bookingId ? patchBookingRun(run, runState) : run
  ));

  const envelope = cached as { data?: { data?: unknown } };
  return {
    ...envelope,
    data: {
      ...envelope.data,
      data: {
        ...cohort,
        bookingRuns,
      },
    },
  };
}
